import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUp,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Coins,
  Copy,
  FileText,
  Gift,
  Headphones,
  Landmark,
  ArrowLeftRight,
  Sparkles,
  ShieldAlert,
  Link2,
  Mail,
  MessageCircle,
  MousePointerClick,
  Percent,
  PieChart,
  TrendingUp,
  Trophy,
  UserPlus,
  Users,
  Wallet,
  QrCode,
} from 'lucide-react';
import PartnerKitIcon from '../../components/common/PartnerKitIcon';
import { getThemeColor } from '../../config/appConfig';
import { useUser } from '../../contexts/UserContext';
import { useKycVerified } from '../../hooks/useKycVerified';
import apiService, { extractClientsList } from '../../utils/apiService';
import { useFetchOnceByKey } from '../../hooks/useMountOnce';
import { parseDashboardWidgets, toAmount } from '../../utils/dashboardWidgets';
import { parseLoyaltySummary } from '../../utils/loyaltyData';
import DashboardTraders from '../dashboard/DashboardTraders';
import { DUMMY_ACCOUNT_MANAGER, mapClientsToTraders } from '../dashboard/dashboardSideData';
import { generateQrDataUrl } from '../qr/qrHelpers';
import { SHARE_LINKS } from '../Dashboard';
import '../dashboard/dashboard-module.css';
import './dashboard2.css';

const BRAND = getThemeColor('primary');
const BLUE = '#2563eb';
const ORANGE = '#f59e0b';
const GREEN = '#16a34a';
const RED = '#ef4444';
const DONUT_COLORS = [BRAND, BLUE, '#f59e0b', GREEN, '#7c3aed', '#64748b'];

/* ───────────────────────── formatters & parsers ───────────────────────── */

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(
    toAmount(value)
  );
const formatNumber = (value) => new Intl.NumberFormat('en-US').format(toAmount(value));
const formatLots = (value) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(toAmount(value));
const formatCompact = (value) =>
  new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(toAmount(value));

const pick = (obj, ...keys) => {
  for (const key of keys) {
    const v = obj?.[key];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
};

const isTruthyFlag = (v) => v === true || v === 1 || String(v).toLowerCase() === 'true';

const parseTransactionWidget = (res) => {
  const d = res?.data?.result ?? res?.data ?? {};
  return {
    availableBalance: toAmount(pick(d, 'availableBalance', 'AvailableBalance')),
    pendingCount: toAmount(pick(d, 'pendingCount', 'PendingCount')),
  };
};

const parseRangeSummary = (res) => {
  const d = res?.data?.result ?? res?.data ?? {};
  const list = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);
  return {
    rebate: toAmount(pick(d, 'rebate', 'Rebate')),
    totalLots: toAmount(pick(d, 'totalLots', 'TotalLots')),
    totalDeposit: toAmount(pick(d, 'totalDeposit', 'TotalDeposit')),
    totalWithdrawal: toAmount(pick(d, 'totalWithdrawal', 'TotalWithdrawal')),
    paidEarnings: toAmount(pick(d, 'paidEarnings', 'PaidEarnings')),
    clientWithdrawal: toAmount(pick(d, 'clientWithdrawal', 'ClientWithdrawal')),
    clientPendingWithdrawals: toAmount(pick(d, 'clientPendingWithdrawals', 'ClientPendingWithdrawals')),
    netDeposit: toAmount(pick(d, 'netDeposit', 'NetDeposit')),
    ftdClients: toAmount(pick(d, 'ftdClients', 'FtdClients')),
    ftdAmount: toAmount(pick(d, 'ftdAmount', 'FtdAmount')),
    pendingWithdrawalCount: toAmount(pick(d, 'pendingWithdrawalCount', 'PendingWithdrawalCount')),
    newClients: toAmount(pick(d, 'newClients', 'NewClients')),
    totalClients: toAmount(pick(d, 'totalClients', 'TotalClients')),
    activeClients: toAmount(pick(d, 'activeClients', 'ActiveClients')),
    newSubIbs: toAmount(pick(d, 'newSubIbs', 'NewSubIbs')),
    totalSubIbs: toAmount(pick(d, 'totalSubIbs', 'TotalSubIbs')),
    activeSubIbs: toAmount(pick(d, 'activeSubIbs', 'ActiveSubIbs')),
    totalClientsAsOf: toAmount(pick(d, 'totalClientsAsOf', 'TotalClientsAsOf')),
    totalSubIbsAsOf: toAmount(pick(d, 'totalSubIbsAsOf', 'TotalSubIbsAsOf')),
    activeSubIbsAsOf: toAmount(pick(d, 'activeSubIbsAsOf', 'ActiveSubIbsAsOf')),
    activeNewSubIbs: toAmount(pick(d, 'activeNewSubIbs', 'ActiveNewSubIbs')),
    depositingClients: toAmount(pick(d, 'depositingClients', 'DepositingClients')),
    loyaltyPointsEarned: toAmount(pick(d, 'loyaltyPointsEarned', 'LoyaltyPointsEarned')),
    aum: toAmount(pick(d, 'aum', 'Aum')),
    aumAccounts: toAmount(pick(d, 'aumAccounts', 'AumAccounts')),
    aumClients: toAmount(pick(d, 'aumClients', 'AumClients')),
    aumNewAccounts: toAmount(pick(d, 'aumNewAccounts', 'AumNewAccounts')),
    topSubIbs: list(pick(d, 'topSubIbs', 'TopSubIbs')).map((t, i) => ({
      id: pick(t, 'affiliateUserId', 'AffiliateUserId') ?? i,
      code: String(pick(t, 'affiliateCode', 'AffiliateCode') || '—'),
      email: String(pick(t, 'email', 'Email') || '—'),
      isActive: isTruthyFlag(pick(t, 'isActive', 'IsActive')),
      newClients: toAmount(pick(t, 'newClients', 'NewClients')),
      lots: toAmount(pick(t, 'lots', 'Lots')),
    })),
    trend: list(pick(d, 'trend', 'Trend')).map((t) => ({
      label: String(pick(t, 'label', 'Label') ?? ''),
      rebate: toAmount(pick(t, 'rebate', 'Rebate')),
      lots: toAmount(pick(t, 'lots', 'Lots')),
      referrals: toAmount(pick(t, 'referrals', 'Referrals')),
    })),
    breakdown: list(pick(d, 'breakdown', 'Breakdown')).map((b) => ({
      label: String(pick(b, 'symbol', 'Symbol') ?? '—'),
      amount: toAmount(pick(b, 'amount', 'Amount')),
      percentage: toAmount(pick(b, 'percentage', 'Percentage')),
    })),
  };
};

/* ── dates ── */
const pad2 = (n) => String(n).padStart(2, '0');
const toISODate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const fromISODate = (s) => {
  const [y, m, d] = String(s).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};
const presetRange = (preset) => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - (preset === '7d' ? 6 : 29));
  return { from: toISODate(from), to: toISODate(to) };
};
const fmtDay = (s) => fromISODate(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const formatPeriod = (range) => `${fmtDay(range.from)} - ${fmtDay(range.to)}`;
const dayOf = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : toISODate(d);
};

const niceMax = (v) => {
  if (!(v > 0)) return 4;
  const pow = 10 ** Math.floor(Math.log10(v));
  const n = v / pow;
  const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return nice * pow;
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

/* ───────────────────────── building blocks ───────────────────────── */

const Skeleton = ({ w = '100%', h = '1rem', r = '0.5rem', style }) => (
  <span className="dx-skel" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

const WhatsAppIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 3.5a8.5 8.5 0 0 0-7.3 12.8L3.5 20.5l4.4-1.1A8.5 8.5 0 1 0 12 3.5zm0 15.3a6.8 6.8 0 0 1-3.5-1l-.3-.1-2.5.6.7-2.4-.2-.3a6.8 6.8 0 1 1 5.3 3.2zm3.7-4.8c-.2-.1-1.3-.6-1.5-.7-.2-.1-.4-.1-.6.1-.1.1-.5.5-.6.7-.1.1-.2.2-.4.1-.2-.1-.8-.3-1.6-1-.6-.5-1-1.2-1.1-1.4-.1-.2 0-.3.1-.5.1-.1.2-.3.3-.4.1-.1.1-.2.2-.3 0-.1 0-.2-.1-.3 0-.1-.5-1.1-.7-1.5-.2-.3-.4-.4-.7-.4h-.6c-.2 0-.5.1-.6.3-.2.2-.7.7-.7 1.5 0 .9.7 1.8.8 2 .1.2 1.4 2.2 3.5 3 .5.2.9.3 1.2.3.4 0 .8-.1 1.1-.4.3-.3 1.2-1.1 1.4-1.4.2-.3 0-.5 0-.6-.1-.1-.2-.1-.4-.2z" />
  </svg>
);

/* Decorative / real sparkline used at the bottom of KPI cards */
const DEFAULT_WAVE = [4, 6, 5, 8, 6, 9, 7, 10, 8, 11];
const Sparkline = ({ values, color }) => {
  const real = Array.isArray(values) && values.length >= 2 && values.some((v) => v > 0);
  const data = real ? values : DEFAULT_WAVE;
  const w = 140;
  const h = 44;
  const max = Math.max(...data, 1);
  const min = real ? Math.min(...data) : 0;
  const span = max - min || 1;
  const pts = data.map((v, i) => [(i / (data.length - 1)) * w, h - 6 - ((v - min) / span) * (h - 14)]);
  const path = pts
    .map((p, i) => {
      if (i === 0) return `M${p[0]},${p[1]}`;
      const prev = pts[i - 1];
      const cx = (prev[0] + p[0]) / 2;
      return `C${cx},${prev[1]} ${cx},${p[1]} ${p[0]},${p[1]}`;
    })
    .join(' ');
  return (
    <svg className="dx-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden>
      <path d={path} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" opacity={real ? 0.85 : 0.45} />
    </svg>
  );
};

const MiniBars = ({ color }) => (
  <svg className="dx-minibars" width="26" height="24" viewBox="0 0 26 24" aria-hidden>
    <rect x="1" y="12" width="5" height="11" rx="1.5" fill={color} opacity="0.55" />
    <rect x="10" y="6" width="5" height="17" rx="1.5" fill={color} opacity="0.75" />
    <rect x="19" y="1" width="5" height="22" rx="1.5" fill={color} />
  </svg>
);

const TopKpi = ({ tone, icon, label, value, loading, error, onClick, children, spark, sparkColor, bars }) => (
  <div className={`dx-kpi is-${tone}`}>
    <div className="dx-kpi__top">
      <span className="dx-kpi__icon">{icon}</span>
      <div className="dx-kpi__text">
        <p className="dx-kpi__label">{label}</p>
        {loading ? <Skeleton h="1.9rem" w="7rem" style={{ marginTop: '0.35rem' }} /> : <p className="dx-kpi__value">{error ? '—' : value}</p>}
      </div>
      <button type="button" className="dx-kpi__go" onClick={onClick} aria-label={`Open ${label}`}>
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
    <div className="dx-kpi__foot">
      {loading ? <Skeleton h="0.75rem" w="60%" /> : error ? <span className="dx-kpi__hint">Unable to load</span> : children}
    </div>
    <Sparkline values={spark} color={sparkColor} />
    {bars ? <MiniBars color={sparkColor} /> : null}
  </div>
);

const InfoCard = ({ tone, icon, label, value, hint, loading, error, onClick, title }) => (
  <div className={`dx-info is-${tone}`} title={title}>
    <span className="dx-info__icon">{icon}</span>
    <div className="min-w-0 flex-1">
      <p className="dx-info__label">{label}</p>
      {loading ? <Skeleton h="1.6rem" w="6rem" style={{ marginTop: '0.3rem' }} /> : <p className="dx-info__value">{error ? '—' : value}</p>}
      {error || hint ? <p className="dx-info__hint">{error ? 'Unable to load' : hint}</p> : null}
    </div>
    <button type="button" className="dx-kpi__go" onClick={onClick} aria-label={`Open ${label}`}>
      <ChevronRight className="w-4 h-4" />
    </button>
  </div>
);

const Panel = ({ icon, title, subtitle, chip, onChip, right, children, className = '' }) => (
  <section className={`dx-card ${className}`}>
    <header className="dx-card__head">
      <span className="dx-card__icon">{icon}</span>
      <div className="min-w-0 flex-1">
        <h2 className="dx-card__title">{title}</h2>
        {subtitle ? <p className="dx-card__sub">{subtitle}</p> : null}
      </div>
      {right}
      {chip ? (
        <button type="button" className="dx-chip" onClick={onChip}>
          {chip} <ChevronDown className="w-3.5 h-3.5" />
        </button>
      ) : null}
    </header>
    {children}
  </section>
);

/* Date filter: one pill that opens 7 days / 30 days / custom range */
const DateRangeFilter = ({ preset, range, onPreset, onCustom }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(range);
  const wrapRef = useRef(null);
  const today = toISODate(new Date());

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const valid = draft.from && draft.to && draft.from <= draft.to && draft.to <= today;
  const choose = (p) => {
    onPreset(p);
    setOpen(false);
  };

  return (
    <div className="dx-filter" ref={wrapRef}>
      <button
        type="button"
        className="dx-datepill"
        onClick={() => {
          setDraft(range);
          setOpen((v) => !v);
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <CalendarDays className="w-4 h-4" />
        <span>{formatPeriod(range)}</span>
        <ChevronDown className="w-3.5 h-3.5" />
      </button>

      {open && (
        <div className="dx-filter__pop" role="dialog" aria-label="Select date range">
          <div className="dx-filter__presets" role="tablist" aria-label="Quick ranges">
            <button type="button" role="tab" aria-selected={preset === '7d'} className={preset === '7d' ? 'is-active' : ''} onClick={() => choose('7d')}>
              7 Days
            </button>
            <button type="button" role="tab" aria-selected={preset === '30d'} className={preset === '30d' ? 'is-active' : ''} onClick={() => choose('30d')}>
              30 Days
            </button>
          </div>
          <p className={`dx-filter__custom ${preset === 'custom' ? 'is-active' : ''}`}>Custom range</p>
          <div className="dx-filter__fields">
            <label>
              <span>From</span>
              <input type="date" value={draft.from} max={draft.to || today} onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))} />
            </label>
            <label>
              <span>To</span>
              <input type="date" value={draft.to} min={draft.from} max={today} onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))} />
            </label>
          </div>
          <div className="dx-filter__actions">
            <button type="button" className="dx-btn dx-btn--ghost dx-btn--sm" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="dx-btn dx-btn--primary dx-btn--sm"
              disabled={!valid}
              onClick={() => {
                onCustom(draft);
                setOpen(false);
              }}
            >
              Apply range
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* Dual-axis trend chart: rebate ($, left axis) vs new clients (count, right axis) */
const TrendChart = ({ labels, earnings, referrals, hasEarnings }) => {
  const [hover, setHover] = useState(null);
  const w = 760;
  const h = 230;
  const pad = { l: 44, r: 44, t: 12, b: 30 };
  const cw = w - pad.l - pad.r;
  const ch = h - pad.t - pad.b;
  const eMax = niceMax(Math.max(...earnings, 0));
  const rMax = niceMax(Math.max(...referrals, 0));
  const toX = (i) => pad.l + (labels.length === 1 ? cw / 2 : (i / (labels.length - 1)) * cw);
  const yE = (v) => pad.t + ch - (v / eMax) * ch;
  const yR = (v) => pad.t + ch - (v / rMax) * ch;
  const line = (arr, y) => arr.map((v, i) => `${toX(i)},${y(v)}`).join(' ');
  const area = (arr, y) => `${toX(0)},${pad.t + ch} ${line(arr, y)} ${toX(arr.length - 1)},${pad.t + ch}`;
  const colW = cw / Math.max(labels.length - 1, 1);
  const earnName = hasEarnings ? 'Rebate' : 'Lots';
  const labelStep = Math.max(1, Math.ceil(labels.length / 10));

  return (
    <div className="dx-chart">
      <svg viewBox={`0 0 ${w} ${h}`} className="dx-chart__svg" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Performance chart">
        <defs>
          <linearGradient id="dxEarn" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={BRAND} stopOpacity="0.18" />
            <stop offset="100%" stopColor={BRAND} stopOpacity="0" />
          </linearGradient>
          <linearGradient id="dxRef" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={RED} stopOpacity="0.16" />
            <stop offset="100%" stopColor={RED} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 1, 2, 3, 4].map((t) => {
          const y = pad.t + ch - (t / 4) * ch;
          return (
            <g key={t}>
              <line x1={pad.l} x2={pad.l + cw} y1={y} y2={y} stroke="#eceff4" />
              <text x={pad.l - 8} y={y + 3.5} textAnchor="end" fontSize="10.5" fill="#94a3b8">
                {formatCompact((eMax / 4) * t)}
              </text>
              <text x={pad.l + cw + 8} y={y + 3.5} textAnchor="start" fontSize="10.5" fill="#94a3b8">
                {formatCompact((rMax / 4) * t)}
              </text>
            </g>
          );
        })}
        {labels.map((m, i) =>
          i % labelStep === 0 ? (
            <text key={`${m}-${i}`} x={toX(i)} y={h - 8} textAnchor="middle" fontSize="10.5" fill="#94a3b8">
              {m}
            </text>
          ) : null
        )}
        {hover != null && <line x1={toX(hover)} x2={toX(hover)} y1={pad.t} y2={pad.t + ch} stroke="#cbd5e1" strokeDasharray="3 4" />}
        <polygon fill="url(#dxEarn)" points={area(earnings, yE)} />
        <polygon fill="url(#dxRef)" points={area(referrals, yR)} />
        <polyline fill="none" stroke={BRAND} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" points={line(earnings, yE)} />
        <polyline fill="none" stroke={RED} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" points={line(referrals, yR)} />
        {labels.length <= 31 && earnings.map((v, i) => (
          <circle key={`e${i}`} cx={toX(i)} cy={yE(v)} r={hover === i ? 5 : 3.2} fill="#fff" stroke={BRAND} strokeWidth="1.8" />
        ))}
        {labels.length <= 31 && referrals.map((v, i) => (
          <circle key={`r${i}`} cx={toX(i)} cy={yR(v)} r={hover === i ? 5 : 3.2} fill="#fff" stroke={ORANGE} strokeWidth="1.8" />
        ))}
        {labels.map((m, i) => (
          <rect
            key={`hit-${m}-${i}`}
            x={toX(i) - colW / 2}
            y={pad.t}
            width={colW}
            height={ch}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          />
        ))}
      </svg>
      {hover != null && (
        <div
          className="dx-chart__tip"
          style={{
            left: `${(toX(hover) / w) * 100}%`,
            transform: `translateX(${hover > labels.length / 2 ? '-105%' : '5%'})`,
          }}
        >
          <p className="dx-chart__tip-title">{labels[hover]}</p>
          <p>
            <i style={{ backgroundColor: BRAND }} />
            {earnName}
            <b>{hasEarnings ? formatCurrency(earnings[hover]) : formatLots(earnings[hover])}</b>
          </p>
          <p>
            <i style={{ backgroundColor: ORANGE }} />
            New clients
            <b>{formatNumber(referrals[hover])}</b>
          </p>
        </div>
      )}
    </div>
  );
};

const Donut = ({ segments, centerValue }) => {
  let offset = 0;
  const C = 2 * Math.PI * 38;
  return (
    <div className="dx-donut">
      <div className="dx-donut__chart">
        <svg viewBox="0 0 100 100" className="dx-donut__svg">
          <circle cx="50" cy="50" r="38" fill="none" stroke="#f1f5f9" strokeWidth="11" />
          {segments.map((seg, i) => {
            const len = (Math.max(seg.percentage, 0) / 100) * C;
            const el = (
              <circle
                key={seg.label}
                cx="50"
                cy="50"
                r="38"
                fill="none"
                stroke={DONUT_COLORS[i % DONUT_COLORS.length]}
                strokeWidth="11"
                strokeDasharray={`${len} ${C}`}
                strokeDashoffset={-offset}
              />
            );
            offset += len;
            return el;
          })}
        </svg>
        <div className="dx-donut__center">
          <span>Total</span>
          <b>{centerValue}</b>
        </div>
      </div>
      <ul className="dx-donut__legend">
        {segments.map((seg, i) => (
          <li key={seg.label}>
            <span className="dx-donut__dot" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
            <span className="dx-donut__name">{seg.label}</span>
            <b>{formatCurrency(seg.amount)}</b>
            <em>{Number(seg.percentage).toFixed(1)}%</em>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ShareIcon = ({ item, url }) => {
  const style = { '--c': item.bg, '--c-hover': item.hover || item.bg };
  const icon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d={item.path} fill="#fff" />
    </svg>
  );
  return url ? (
    <a className="dx-share" style={style} href={item.href(url)} target="_blank" rel="noreferrer" aria-label={item.label} title={item.label}>
      {icon}
    </a>
  ) : (
    <span className="dx-share is-disabled" style={style} aria-label={item.label} title={item.label}>
      {icon}
    </span>
  );
};

/* ───────────────────────── page ───────────────────────── */

const INITIAL = { status: 'loading', data: null };

const Dashboard2 = () => {
  const navigate = useNavigate();
  const { userData } = useUser();
  const { kycVerified, loading: kycLoading } = useKycVerified();
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [preset, setPreset] = useState('7d');
  const [range, setRange] = useState(() => presetRange('7d'));
  const [src, setSrc] = useState({
    widgets: INITIAL,
    txn: INITIAL,
    clients: INITIAL,
    loyalty: INITIAL,
    range: INITIAL,
  });

  const load = useCallback(async (key, request, parse, cancelled) => {
    try {
      const res = await request();
      if (cancelled()) return;
      const ok = res != null && res.success !== false;
      setSrc((prev) => ({
        ...prev,
        [key]: ok ? { status: 'ready', data: parse(res) } : { status: 'error', data: prev[key].data },
      }));
    } catch {
      if (!cancelled()) setSrc((prev) => ({ ...prev, [key]: { status: 'error', data: prev[key].data } }));
    }
  }, []);

  /* Figures that do not depend on the date filter */
  const fetchStatic = useCallback(
    async ({ cancelled }) => {
      await Promise.all([
        load('widgets', () => apiService.getDashboardWidgets(), (r) => parseDashboardWidgets(r.data), cancelled),
        load('txn', () => apiService.getTransactionWidget(), parseTransactionWidget, cancelled),
        load(
          'loyalty',
          () => apiService.getLoyaltySummary(),
          (r) => {
            const p = parseLoyaltySummary(r.data);
            return p.availableLoyaltyPoints ?? p.loyaltyPoints ?? 0;
          },
          cancelled
        ),
        load(
          'clients',
          () => apiService.getClientsList({ pageNumber: 1, pageSize: 100, searchTerm: '', type: '', sortBy: '', sortOrder: '' }),
          (r) => extractClientsList(r),
          cancelled
        ),
      ]);
    },
    [load]
  );

  /* Everything that follows the selected date range */
  const fetchRange = useCallback(
    async ({ cancelled }) => {
      setRefreshing(true);
      setSrc((prev) => ({ ...prev, range: { status: 'loading', data: prev.range.data } }));
      await load('range', () => apiService.getDashboardRangeSummary(range.from, range.to), parseRangeSummary, cancelled);
      if (!cancelled()) setRefreshing(false);
    },
    [load, range.from, range.to]
  );

  useFetchOnceByKey(`dashboard-2-static-${refreshKey}`, fetchStatic, true);
  useFetchOnceByKey(`dashboard-2-range-${range.from}-${range.to}-${refreshKey}`, fetchRange, true);

  const widgets = src.widgets.data;
  const txn = src.txn.data;
  const rs = src.range.data;

  const loading = (k) => src[k].status === 'loading';
  const failed = (k) => src[k].status === 'error' && !src[k].data;
  const rangeLoading = loading('range');
  const rangeFailed = failed('range');

  const referralLink = widgets?.partnerUrl || '';
  const encodedLink = encodeURIComponent(referralLink);

  useEffect(() => {
    let alive = true;
    if (!referralLink) {
      setQrUrl('');
      return undefined;
    }
    generateQrDataUrl(referralLink)
      .then((url) => alive && setQrUrl(url))
      .catch(() => alive && setQrUrl(''));
    return () => {
      alive = false;
    };
  }, [referralLink]);

  /* ── chart ── */
  const chart = useMemo(() => {
    const trend = rs?.trend ?? [];
    if (!trend.length) return null;
    const hasRebate = trend.some((t) => t.rebate > 0);
    const hasLots = trend.some((t) => t.lots > 0);
    const useRebate = hasRebate || !hasLots;
    return {
      labels: trend.map((t) => t.label),
      hasEarnings: useRebate,
      earnings: trend.map((t) => (useRebate ? t.rebate : t.lots)),
      referrals: trend.map((t) => t.referrals),
    };
  }, [rs]);

  const donutSegments = useMemo(() => {
    const list = (rs?.breakdown ?? []).filter((b) => b.amount > 0).sort((a, b) => b.amount - a.amount);
    if (!list.length) return [];
    const total = list.reduce((sum, b) => sum + b.amount, 0);
    const top = list.slice(0, 5);
    const rest = list.slice(5).reduce((sum, b) => sum + b.amount, 0);
    const rows = rest > 0 ? [...top, { label: 'Others', amount: rest }] : top;
    return rows.map((b) => ({ label: b.label, amount: b.amount, percentage: total > 0 ? Number(((b.amount / total) * 100).toFixed(1)) : 0 }));
  }, [rs]);

  const rebateSpark = useMemo(() => (rs?.trend ?? []).map((t) => t.rebate), [rs]);

  /* ── performance footer stats ── */
  const perfStats = useMemo(() => {
    const total = rs?.totalClientsAsOf ?? 0;
    const rate = total > 0 ? (toAmount(rs?.activeClients) / total) * 100 : null;
    return [
      { label: 'Total Referrals', value: formatNumber(total), icon: <Users className="w-[18px] h-[18px]" /> },
      { label: 'Active Clients', value: formatNumber(rs?.activeClients ?? 0), icon: <TrendingUp className="w-[18px] h-[18px]" /> },
      { label: 'Trading Volume', value: `${formatLots(rs?.totalLots ?? 0)} lots`, icon: <MousePointerClick className="w-[18px] h-[18px]" /> },
      { label: 'Conversion Rate', value: rate != null ? `${rate.toFixed(1)}%` : '—', icon: <Percent className="w-[18px] h-[18px]" /> },
    ];
  }, [rs]);

  /* ── financial flow (period figures + current balance) ── */
  const flow = useMemo(() => {
    const rebate = rs?.rebate ?? 0;
    const withdrawn = rs?.totalWithdrawal ?? 0;
    const items = [
      { label: 'Client Deposits', value: rs?.totalDeposit ?? 0, color: BLUE },
      { label: 'Rebate Earned', value: rebate, color: GREEN },
      { label: 'Paid out to you', value: withdrawn, color: RED },
      { label: 'Available Balance (now)', value: txn?.availableBalance ?? 0, color: ORANGE },
    ];
    const total = items.reduce((s, i) => s + i.value, 0);
    const payout = rebate > 0 ? Math.min(100, (withdrawn / rebate) * 100) : 0;
    return { items, total, payout };
  }, [rs, txn]);

  const topSubs = useMemo(() => (rs?.topSubIbs ?? []).slice(0, 3), [rs]);

  /* Recent clients registered inside the selected range */
  const traders = useMemo(() => {
    const raw = src.clients.data || [];
    const regOf = (c) => c.userRegistrationDate ?? c.UserRegistrationDate ?? c.registrationDate ?? c.createdDate;
    const inRange = raw
      .filter((c) => {
        const day = dayOf(regOf(c));
        return day && day >= range.from && day <= range.to;
      })
      .sort((a, b) => new Date(regOf(b) ?? 0) - new Date(regOf(a) ?? 0));
    return mapClientsToTraders(inRange).slice(0, 10);
  }, [src.clients.data, range]);

  const displayName = userData?.firstName || userData?.fullName?.split(' ')[0] || 'Partner';
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const manager = DUMMY_ACCOUNT_MANAGER;

  const handleCopy = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const handleRefresh = () => {
    if (!refreshing) setRefreshKey((k) => k + 1);
  };

  const handlePreset = (p) => {
    setPreset(p);
    setRange(presetRange(p));
  };

  const handleCustom = (r) => {
    setPreset('custom');
    setRange(r);
  };

  const totalClients = rs?.totalClientsAsOf ?? widgets?.totalClients ?? 0;
  const paidInPeriod = rs?.totalWithdrawal ?? 0;
  const pendingInPeriod = Math.max(0, (rs?.rebate ?? 0) - paidInPeriod);
  // Original dashboard rules: paid earnings inside the range, pending = wallet balance.
  const pendingEarnings = widgets?.pendingCommission ?? txn?.availableBalance ?? 0;

  return (
    <div className="dash-page dx-page">
      {/* Header */}
      <header className="dx-header">
        <div className="min-w-0">
          <p className="dx-header__date">{today}</p>
          <h1 className="dx-header__title">
            {greeting()}, {displayName} <span className="dx-header__wave" aria-hidden>👋</span>
          </h1>
          <p className="dx-header__sub">Here is a live snapshot of your rebate, clients and sub IB network.</p>
        </div>
        <div className="dx-header__actions">
          <div className="dx-filterwrap">
            <p className="dx-header__period">
              <span className="dx-header__period-dot" aria-hidden />
              Showing <b>{preset === '7d' ? 'Last 7 days' : preset === '30d' ? 'Last 30 days' : 'Custom range'}</b>
              <span>{formatPeriod(range)}</span>
            </p>
            <DateRangeFilter preset={preset} range={range} onPreset={handlePreset} onCustom={handleCustom} />
          </div>
        </div>
      </header>

      {/* Alert: shown only while the partner's KYC is pending */}
      {!kycLoading && !kycVerified ? (
        <section className="dx-alert" role="alert" aria-live="polite">
          <span className="dx-alert__icon"><ShieldAlert className="w-[18px] h-[18px]" strokeWidth={2.4} /></span>
          <p className="dx-alert__text">
            <b>KYC pending</b>
            <span>Your account is not verified yet. Complete your KYC to get fully verified.</span>
          </p>
          <button type="button" className="dx-alert__cta" onClick={() => navigate('/kyc')}>
            Complete KYC <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </section>
      ) : null}

      {/* Row 1: money KPIs */}
      <section className="dx-row dx-row--5" aria-label="Key figures">
        <TopKpi
          tone="rebate"
          icon={<Percent className="w-6 h-6" strokeWidth={2.4} />}
          label="Total Rebate"
          value={formatCurrency(rs?.rebate ?? 0)}
          loading={rangeLoading}
          error={rangeFailed}
          onClick={() => navigate('/earnings/dashboard')}
          spark={rebateSpark}
          sparkColor={RED}
        >
          <div className="dx-kpi__split">
            <span><i style={{ background: BRAND }} />Paid</span>
            <b>{formatCurrency(paidInPeriod)}</b>
          </div>
          <div className="dx-kpi__split">
            <span><i style={{ background: RED }} />Pending</span>
            <b>{formatCurrency(pendingInPeriod)}</b>
          </div>
        </TopKpi>
        <TopKpi
          tone="deposit"
          icon={<Wallet className="w-6 h-6" strokeWidth={2.2} />}
          label="Total Deposit"
          value={formatCurrency(rs?.totalDeposit ?? 0)}
          loading={rangeLoading}
          error={rangeFailed}
          onClick={() => navigate('/clients')}
          sparkColor={GREEN}
          bars
        >
          <span className="dx-kpi__hint"><Users className="w-3.5 h-3.5" /> From {formatNumber(rs?.depositingClients ?? 0)} client{rs?.depositingClients === 1 ? '' : 's'}</span>
        </TopKpi>
        <TopKpi
          tone="withdrawal"
          icon={<ArrowUp className="w-6 h-6" strokeWidth={2.6} />}
          label="Total Withdrawal"
          value={formatCurrency(rs?.clientWithdrawal ?? 0)}
          loading={rangeLoading}
          error={rangeFailed}
          onClick={() => navigate('/clients')}
          sparkColor="#7c3aed"
          bars
        >
          <span className="dx-kpi__hint">
            <Users className="w-3.5 h-3.5" />
            {rs?.clientPendingWithdrawals > 0 ? `${formatNumber(rs.clientPendingWithdrawals)} request(s) pending` : 'No pending requests'}
          </span>
        </TopKpi>
        <TopKpi
          tone="netdep"
          icon={<ArrowLeftRight className="w-6 h-6" strokeWidth={2.4} />}
          label="Net Deposit"
          value={formatCurrency(rs?.netDeposit ?? 0)}
          loading={rangeLoading}
          error={rangeFailed}
          onClick={() => navigate('/clients')}
          sparkColor="#0d9488"
          bars
        >
          <div className="dx-kpi__split">
            <span><i style={{ background: BLUE }} />Deposit</span>
            <b>{formatCurrency(rs?.totalDeposit ?? 0)}</b>
          </div>
          <div className="dx-kpi__split">
            <span><i style={{ background: RED }} />Withdrawal</span>
            <b>− {formatCurrency(rs?.clientWithdrawal ?? 0)}</b>
          </div>
        </TopKpi>
        <TopKpi
          tone="subibs"
          icon={<Users className="w-6 h-6" strokeWidth={2.2} />}
          label="Total Sub IBs"
          value={formatNumber(rs?.newSubIbs ?? 0)}
          loading={rangeLoading}
          error={rangeFailed}
          onClick={() => navigate('/clients/sub-partners')}
          sparkColor={ORANGE}
          bars
        >
          <span className="dx-kpi__hint">
            <Users className="w-3.5 h-3.5" />
            {formatNumber(rs?.activeNewSubIbs ?? 0)} active · {formatNumber(rs?.totalSubIbsAsOf ?? 0)} total
          </span>
        </TopKpi>
      </section>

      {/* AUM: assets under management of partner-linked clients */}
      <section className="dx-aum" aria-label="Assets under management">
        <div className="dx-aum__main">
          <span className="dx-aum__icon"><Landmark className="w-7 h-7" strokeWidth={2} /></span>
          <div className="min-w-0">
            <p className="dx-aum__label">
              Assets Under Management (AUM)
              <span className="dx-aum__tag">As of {fmtDay(range.to)}</span>
            </p>
            {rangeLoading ? (
              <Skeleton h="2.4rem" w="12rem" style={{ marginTop: '0.4rem' }} />
            ) : (
              <p className="dx-aum__value">{rangeFailed ? '—' : formatCurrency(rs?.aum ?? 0)}</p>
            )}
            <p className="dx-aum__hint">Combined portfolio value of your linked clients&apos; live accounts up to the selected end date.</p>
          </div>
        </div>
        <dl className="dx-aum__stats">
          <div>
            <dt>Clients with funds</dt>
            <dd>{rangeLoading ? '…' : formatNumber(rs?.aumClients ?? 0)}</dd>
          </div>
          <div>
            <dt>Live accounts</dt>
            <dd>{rangeLoading ? '…' : formatNumber(rs?.aumAccounts ?? 0)}</dd>
          </div>
        </dl>
      </section>

      {/* Row 2: client / earnings info */}
      <section className="dx-row dx-row--6" aria-label="Clients and earnings">
        <InfoCard
          tone="clients"
          icon={<Users className="w-6 h-6" strokeWidth={2.2} />}
          label="Total Clients"
          value={formatNumber(totalClients)}
          hint={`${formatNumber(rs?.activeClients ?? 0)} active · ${formatLots(rs?.totalLots ?? 0)} lots`}
          loading={rangeLoading}
          error={rangeFailed}
          onClick={() => navigate('/clients')}
        />
        <InfoCard
          tone="newclients"
          icon={<UserPlus className="w-6 h-6" strokeWidth={2.2} />}
          label="New Clients"
          value={formatNumber(rs?.newClients ?? 0)}
          hint="Registered in this period"
          loading={rangeLoading}
          error={rangeFailed}
          onClick={() => navigate('/clients')}
        />
        <InfoCard
          tone="ftd"
          icon={<Sparkles className="w-6 h-6" strokeWidth={2.2} />}
          label="FTD"
          title="First Time Deposit: clients whose first-ever approved deposit falls in the selected period"
          value={formatNumber(rs?.ftdClients ?? 0)}
          hint={`FTD amount ${formatCurrency(rs?.ftdAmount ?? 0)}`}
          loading={rangeLoading}
          error={rangeFailed}
          onClick={() => navigate('/clients')}
        />
        <InfoCard
          tone="earnings"
          icon={<Coins className="w-6 h-6" strokeWidth={2.2} />}
          label="Total Earnings"
          value={formatCurrency(rs?.paidEarnings ?? 0)}
          hint="Paid out to your wallet"
          loading={rangeLoading}
          error={rangeFailed}
          onClick={() => navigate('/earnings/list')}
        />
        <InfoCard
          tone="pending"
          icon={<Clock className="w-6 h-6" strokeWidth={2.2} />}
          label="Pending Earnings"
          value={formatCurrency(pendingEarnings)}
          loading={loading('widgets') && loading('txn')}
          error={failed('widgets') && failed('txn')}
          onClick={() => navigate('/withdrawal')}
        />
        <InfoCard
          tone="loyalty"
          icon={<Gift className="w-6 h-6" strokeWidth={2.2} />}
          label="Loyalty Points"
          value={formatNumber(src.loyalty.data ?? 0)}
          hint="Redeem rewards"
          loading={loading('loyalty')}
          error={failed('loyalty')}
          onClick={() => navigate('/loyalty-points')}
        />
      </section>

      {/* Row 4: performance + financial flow */}
      <div className="dx-grid dx-grid--main">
        <Panel
          icon={<TrendingUp className="w-5 h-5" />}
          title="Performance Overview"
          subtitle="Monthly earnings against new referrals"
          chip="Earning Report"
          onChip={() => navigate('/earnings/dashboard')}
          className="dx-fill"
        >
          <div className="dx-legend">
            <span><i style={{ backgroundColor: BRAND }} />{chart?.hasEarnings === false ? 'Lots' : 'Rebate'} (left axis)</span>
            <span><i style={{ backgroundColor: ORANGE }} />Referrals (right axis)</span>
          </div>
          {rangeLoading ? (
            <Skeleton h="210px" r="0.85rem" />
          ) : chart ? (
            <TrendChart {...chart} />
          ) : (
            <div className="dx-empty">No performance data for this period</div>
          )}
          <div className="dx-pstats">
            {perfStats.map((s) => (
              <div key={s.label} className="dx-pstat">
                <span className="dx-pstat__icon">{s.icon}</span>
                <div className="min-w-0">
                  <p>{s.label}</p>
                  <b>{rangeLoading ? '…' : rangeFailed ? '—' : s.value}</b>
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel
          icon={<Wallet className="w-5 h-5" />}
          title="Financial Flow"
          subtitle="Deposits, rebate and payouts at a glance"
          chip="Payouts"
          onChip={() => navigate('/payout-history')}
          className="dx-fill"
        >
          {rangeLoading ? (
            <Skeleton h="230px" r="0.85rem" />
          ) : (
            <>
              <ul className="dx-flow">
                {flow.items.map((item) => (
                  <li key={item.label}>
                    <span><i style={{ backgroundColor: item.color }} />{item.label}</span>
                    <b>{formatCurrency(item.value)}</b>
                  </li>
                ))}
              </ul>
              <div className="dx-flow__bar" aria-hidden>
                {flow.total > 0
                  ? flow.items.map((item) => (
                      <span key={item.label} style={{ width: `${(item.value / flow.total) * 100}%`, backgroundColor: item.color }} />
                    ))
                  : <span style={{ width: '100%', backgroundColor: '#f1f4f8' }} />}
              </div>
              <div className="dx-payout">
                <div className="dx-payout__row">
                  <strong>PAYOUT RATIO</strong>
                  <span>Share of your rebate already withdrawn</span>
                </div>
                <div className="dx-payout__bar">
                  <div className="dx-payout__track"><span style={{ width: `${flow.payout}%` }} /></div>
                  <b>{flow.payout.toFixed(0)}%</b>
                </div>
              </div>
            </>
          )}
        </Panel>
      </div>

      {/* Row 5: earnings snapshot, top sub IBs, traders */}
      <div className="dx-grid dx-grid--3">
        <Panel
          icon={<PieChart className="w-5 h-5" />}
          title="Earnings Snapshot"
          subtitle="Rebate by instrument"
          right={<button type="button" className="dx-link" onClick={() => navigate('/earnings/dashboard')}>Details</button>}
          className="dx-fill"
        >
          {rangeLoading ? (
            <Skeleton h="230px" r="0.85rem" />
          ) : donutSegments.length > 0 ? (
            <Donut segments={donutSegments} centerValue={formatCurrency(rs?.paidEarnings ?? 0)} />
          ) : (
            <div className="dx-emptybox">
              <FileText className="w-9 h-9" />
              <p>No earnings breakdown available</p>
              <span>Start referring clients to see your earnings here.</span>
            </div>
          )}
        </Panel>

        <Panel
          icon={<Users className="w-5 h-5" />}
          title="Top Sub IBs"
          subtitle={`Sub IBs joined · ${formatPeriod(range)}`}
          right={<button type="button" className="dx-link" onClick={() => navigate('/clients/sub-partners')}>View all</button>}
          className="dx-fill"
        >
          {rangeLoading ? (
            <div className="dx-skel-list">
              {[0, 1, 2].map((i) => <Skeleton key={i} h="3.2rem" r="0.8rem" />)}
            </div>
          ) : topSubs.length === 0 ? (
            <div className="dx-emptybox">
              <Users className="w-9 h-9" />
              <p>No sub partners yet</p>
              <span>Invite partners to grow your network.</span>
            </div>
          ) : (
            <ul className="dx-subs">
              {topSubs.map((s, i) => (
                <li key={s.id}>
                  <span className="dx-subs__rank">{i + 1}</span>
                  <span className={`dx-subs__avatar is-${i % 3}`}>{(s.code[0] || s.email[0] || 'S').toUpperCase()}</span>
                  <div className="min-w-0 flex-1">
                    <p className="dx-subs__name">{s.code !== '—' ? s.code : s.email}</p>
                    <p className="dx-subs__sub">{formatLots(s.lots)} lots · {s.email}</p>
                  </div>
                  <div className="dx-subs__stats">
                    <b>{formatNumber(s.newClients)}</b>
                    <span>new clients</span>
                  </div>
                  <span className={`dx-pill ${s.isActive ? 'is-on' : 'is-off'}`}>{s.isActive ? 'Active' : 'Inactive'}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <div className="dx-traders">
          <DashboardTraders traders={traders} loading={loading('clients')} onRefresh={handleRefresh} />
        </div>
      </div>

      {/* Row 6: manager, shortcuts, help */}
      <div className="dx-grid dx-grid--bottom">
        <section className="dx-card dx-manager">
          <header className="dx-manager__head">
            <span className="dx-card__icon"><Users className="w-5 h-5" /></span>
            <h2 className="dx-card__title">Account Manager Info</h2>
            <span className="dx-info-dot" title="Get to know your partner account manager and contact them any time.">i</span>
          </header>
          <div className="dx-manager__body">
            <div className="dx-manager__item">
              <span className="dx-manager__icon"><Mail className="w-5 h-5" /></span>
              <div className="min-w-0">
                <small>{manager.name}</small>
                <a href={`mailto:${manager.email}`}>{manager.email}</a>
              </div>
            </div>
            <div className="dx-manager__item">
              <span className="dx-manager__icon is-wa"><WhatsAppIcon /></span>
              <div className="min-w-0">
                <small>WhatsApp</small>
                <a href={`https://wa.me/${manager.phone.replace(/[^\d]/g, '')}`} target="_blank" rel="noreferrer">{manager.phone}</a>
              </div>
            </div>
          </div>
        </section>

        <section className="dx-card dx-shortcuts">
          <button type="button" className="dx-shortcut" onClick={() => navigate('/promotions/list')}>
            <span className="dx-shortcut__icon"><Gift className="w-5 h-5" /></span>
            <span className="min-w-0 flex-1 text-left">
              <b>Promotions & Campaigns</b>
              <small>Boost conversions with active offers</small>
            </span>
            <em>Explore <ArrowRight className="w-3.5 h-3.5" /></em>
          </button>
          <button type="button" className="dx-shortcut" onClick={() => navigate('/rewards/dashboard')}>
            <span className="dx-shortcut__icon"><Trophy className="w-5 h-5" /></span>
            <span className="min-w-0 flex-1 text-left">
              <b>Rewards Center</b>
              <small>Claim milestones and loyalty rewards</small>
            </span>
            <em>View <ArrowRight className="w-3.5 h-3.5" /></em>
          </button>
        </section>

        <section className="dx-card dx-help">
          <div className="dx-help__body">
            <span className="dx-help__icon"><Headphones className="w-6 h-6" /></span>
            <div>
              <h3>Need Help?</h3>
              <p>Chat with our support team anytime.</p>
            </div>
          </div>
          <button type="button" className="dx-help__cta" onClick={() => navigate('/support')}>
            <MessageCircle className="w-4 h-4" />
            Chat Now
          </button>
        </section>
      </div>

      {/* Row 7: referral + quick share (bottom) */}
      <div className="dx-grid dx-grid--referral">
        <section className="dx-referral">
          <div className="dx-referral__main">
            <div className="dx-referral__head">
              <span className="dx-referral__icon"><Link2 className="w-6 h-6" /></span>
              <div className="min-w-0">
                <h2>Your Referral Link</h2>
                <p>Share your unique link and grow your network of traders.</p>
              </div>
            </div>
            <div className="dx-linkfield">
              {loading('widgets') ? <Skeleton h="1rem" w="70%" /> : <span title={referralLink}>{referralLink || '—'}</span>}
              <button type="button" onClick={handleCopy} disabled={!referralLink} aria-label="Copy referral link">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy Link'}
              </button>
            </div>
          </div>
          <div className="dx-referral__qr">
            {qrUrl ? <img src={qrUrl} alt="Referral link QR code" /> : <QrCode className="w-10 h-10" />}
            <span>Scan QR</span>
          </div>
          <div className="dx-referral__art" aria-hidden>
            <PartnerKitIcon name="referralGiftIllustration" size={150} alt="" className="object-contain" style={{ width: 170, height: 140 }} />
          </div>
        </section>

        <section className="dx-card dx-share-card">
          <h2 className="dx-share-card__title">Quick Share</h2>
          <div className="dx-share-card__icons">
            {SHARE_LINKS.map((item) => (
              <ShareIcon key={item.id} item={item} url={referralLink ? encodedLink : ''} />
            ))}
          </div>
          <div className="dx-reflinks">
            <button type="button" onClick={() => navigate('/qr-code/client')}>
              <QrCode className="w-4 h-4" /> Client QR
            </button>
            <button type="button" onClick={() => navigate('/refer-a-friend')}>
              <UserPlus className="w-4 h-4" /> Refer Sub Partner
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard2;
